
import React from 'react';
import { motion } from 'framer-motion';

export const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost' }> = ({ children, variant = 'primary', className = '', ...props }) => {
  // M3 Base: standard 40px height (~py-2 px-6), fully rounded (pill), text-sm font-medium, tracked layout
  const baseStyle = "px-6 py-2.5 rounded-full font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-30 disabled:cursor-not-allowed text-sm relative overflow-hidden group transition-all duration-200 ease-in-out flex items-center justify-center gap-2";
  
  const variants = {
    // M3 Filled Button
    primary: "bg-md-primary text-md-on-primary hover:shadow-md hover:-translate-y-[1px] active:shadow-sm active:translate-y-0",
    // M3 Tonal Button (Mapping secondary to tonal container)
    secondary: "bg-md-secondary-container text-md-on-secondary-container hover:bg-slate-300/80 hover:-translate-y-[1px] active:bg-slate-300",
    // M3 Outlined Button
    outline: "border border-md-outline bg-transparent text-md-primary hover:bg-md-primary-container/30 focus-visible:ring-md-primary",
    // M3 Error/Danger Button
    danger: "bg-md-error text-md-on-error hover:shadow-md hover:-translate-y-[1px] active:shadow-sm",
    // M3 Text Button
    ghost: "bg-transparent text-md-primary hover:bg-md-primary-container/50 px-4"
  };

  return (
    <motion.button 
      whileTap={{ scale: 0.98 }}
      className={`${baseStyle} ${variants[variant]} ${className}`} 
      {...props as any}
    >
      {/* State layer overlay for M3 ripple-like hover */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-white/10 group-active:bg-black/10 transition-colors pointer-events-none"></div>
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </motion.button>
  );
};

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label: string }> = ({ label, className = '', ...props }) => (
  // M3 Filled Text Field Spec
  <div className="mb-5 relative group">
    <div className="relative rounded-t-lg bg-md-surface-variant/50 hover:bg-md-surface-variant transition-colors overflow-hidden border-b border-md-outline group-focus-within:border-md-primary group-focus-within:border-b-2">
        <label className="absolute left-4 top-1.5 text-[10px] font-medium text-md-on-surface-variant group-focus-within:text-md-primary transition-colors">{label}</label>
        <input 
        className={`w-full px-4 pt-6 pb-2 bg-transparent text-md-on-surface focus:outline-none text-sm ${className}`} 
        {...props} 
        />
    </div>
  </div>
);

export const Card: React.FC<{ children: React.ReactNode; className?: string; hover?: boolean; onClick?: () => void; style?: React.CSSProperties }> = ({ children, className = '', hover = false, onClick, style }) => (
  // M3 Card Spec: 24px corner radius (rounded-3xl), surface-container-low or surface
  <motion.div 
    whileHover={hover ? { y: -2, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)" } : {}}
    onClick={onClick}
    style={style}
    className={`bg-md-surface border border-md-outline-variant/50 rounded-3xl shadow-sm ${className} ${onClick ? 'cursor-pointer' : ''}`}
  >
    {children}
  </motion.div>
);

export const ProgressBar: React.FC<{ percentage: number; color?: string; height?: string }> = ({ percentage, color = 'bg-md-primary', height = 'h-2' }) => (
  <div className={`w-full bg-md-surface-variant rounded-full ${height} overflow-hidden`}>
    <motion.div 
      initial={{ width: 0 }}
      animate={{ width: `${percentage}%` }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`${color} ${height} rounded-full`}
    ></motion.div>
  </div>
);

export const Badge: React.FC<{ children: React.ReactNode; color?: 'green' | 'blue' | 'red' | 'yellow' }> = ({ children, color = 'blue' }) => {
    // M3 Badge/Chip mapping (using semantic text and subtle tonal backgrounds)
    const colors = {
        green: 'bg-md-tertiary-container text-md-on-tertiary-container',
        blue: 'bg-md-primary-container text-md-on-primary-container',
        red: 'bg-md-error-container text-md-on-error-container',
        yellow: 'bg-[#ffebd2] text-[#5e4200]' // Equivalent to warning container
    };
    return (
        <span className={`px-3 py-1 rounded-full text-[10px] font-medium tracking-wide ${colors[color]}`}>
            {children}
        </span>
    );
};
